import React, { useState, useEffect, useRef } from "react";
import { HiOutlineUpload, HiOutlineCalendar } from "react-icons/hi";

import {
  MdLocalShipping,
  MdDirectionsCar,
  MdPersonOutline,
  MdBarChart,
  MdAttachMoney,
  MdAssignment,
  MdPayment,
  MdEdit,
  MdAccountCircle,
  MdLibraryBooks,
  MdChecklist,
  MdHowToReg,
  MdEvent, 
} from "react-icons/md";

import "./Sidebar.css";
import "../../Pages/HomePage/HomePage.css";
import { useNavigate } from "react-router-dom";
import AlertModal from "../AlertModal/AlertModal";
import useAuth from "../../Auth/useAuth";
import mftlogo from "../../Images/logo.jpg";

const Sidebar = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [locked, setLocked] = useState(false);

  const {
    username,
    userProfile,
    moduleChange,
    setModuleChange,
    activeModule,
    setActiveModule,
  } = useAuth();
  const sidebarRef = useRef(null);
  const menuRef = useRef(null); // Ref for the scrollable menu container
  const activeItemRef = useRef(null); // Ref for the active menu item

  useEffect(() => {
    if (moduleChange) {
      sessionStorage.setItem("actMod", "upload");
      setActiveModule("upload");
      setModuleChange("");
    }
  }, [moduleChange, setActiveModule, setModuleChange]);

  // Auto-scroll to active item when activeModule changes
  useEffect(() => {
    if (activeItemRef.current && menuRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [activeModule]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !locked
      ) {
        setExpanded(false);
      }
    };

    if (expanded && !locked) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expanded, locked]);

  // // Approval request state
  // const [approvalRequest, setApprovalRequest] = useState({
  //   module: "Freight",
  //   username: username,
  //   profile: userProfile,
  //   action: "",
  //   args: {},
  // });

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Modal handler functions
  const showModal = (title, message, type = "info") => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const handleNavigation = (module, path) => {
    const restrictedModules = {
      loadingManager: [
        "freight",
        "reports",
        "business",
        "transactions",
        "generate-bill",
        "revise-bill",
        "order-book",
      ],
      unloadingManager: [
        "freight",
        "business",
        "vehicle",
        "vendor",
        "transactions",
        "generate-bill",
        "revise-bill",
        "order-book",
      ],
      driverAssociate: [
        "upload",
        "today-list",
        "freight",
        "business",
        "vehicle",
        "vendor",
        "transactions",
        "generate-bill",
        "revise-bill",
        "order-book",
      ],
    };
    if (restrictedModules[userProfile]?.includes(module)) {
      showModal(
        "Access Denied",
        `You do not have permission to access the ${module.toUpperCase()} module.`,
        "error",
      );
      return;
    }

    navigate(path);
    if (!locked) {
      setExpanded(false);
    }
  };

  const handleMouseEnter = () => {
    if (!locked) {
      setExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    if (!locked) {
      setExpanded(false);
    }
  };

  const toggleLock = (e) => {
    e.stopPropagation();
    setLocked((prevLocked) => !prevLocked);
    setExpanded((prevExpanded) => !prevExpanded);
  };

  return (
    <aside
      ref={sidebarRef}
      className={`sidebar ${expanded ? "expanded" : "collapsed"}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="container-freight">
        <AlertModal
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
        />
      </div>
      <div className="sidebar-header">
        {expanded ? (
          <div className="logo-container">
            <img src={mftlogo} alt="Logo" className="logo-image" />
            <span className="app-name">Mindfuturetech</span>
          </div>
        ) : (
          <img src={mftlogo} alt="Logo" className="logo-image-small" />
        )}
      </div>

      <ul className="sidebar-menu" ref={menuRef}>
        <li
          ref={activeModule === "upload" ? activeItemRef : null}
          className={`sidebar-item ${activeModule === "upload" ? "active" : ""}`}
          onClick={() => handleNavigation("upload", "/home")}
        >
          <HiOutlineUpload className="icon" />
          {expanded && <span>Upload Trip Details</span>}
        </li>
        <li
          ref={activeModule === "today-list" ? activeItemRef : null}
          className={`sidebar-item ${activeModule === "today-list" ? "active" : ""}`}
          onClick={() => handleNavigation("today-list", "/today-list")}
        >
          <HiOutlineCalendar className="icon" />
          {expanded && <span>Today's List</span>}
        </li>
        <li
          ref={activeModule === "freight" ? activeItemRef : null}
          className={`sidebar-item freight ${activeModule === "freight" ? "active" : ""}`}
          onClick={() => handleNavigation("freight", "/freight")}
        >
          <MdLocalShipping className="icon" />
          {expanded && <span>Freight</span>}
        </li>
        <li
          ref={activeModule === "vehicle" ? activeItemRef : null}
          className={`sidebar-item vehicle ${activeModule === "vehicle" ? "active" : ""}`}
          onClick={() => handleNavigation("vehicle", "/vehicle")}
        >
          <MdDirectionsCar className="icon" />
          {expanded && <span>Vehicle</span>}
        </li>
        <li
          ref={activeModule === "vendor" ? activeItemRef : null}
          className={`sidebar-item vendor ${activeModule === "vendor" ? "active" : ""}`}
          onClick={() => handleNavigation("vendor", "/vendor")}
        >
          <MdPersonOutline className="icon" />
          {expanded && <span>Vendor</span>}
        </li>
        <li
          ref={activeModule === "meetings" ? activeItemRef : null}
          className={`sidebar-item meetings ${activeModule === "meetings" ? "active" : ""}`}
          onClick={() => handleNavigation("meetings", "/meetings")}
        >
          <MdEvent  className="icon" />
          {expanded && <span>Meetings</span>}
        </li>
        <li
          ref={activeModule === "reports" ? activeItemRef : null}
          className={`sidebar-item repo ${activeModule === "reports" ? "active" : ""}`}
          onClick={() => handleNavigation("reports", "/reports")}
        >
          <MdBarChart className="icon" />
          {expanded && <span>Reports</span>}
        </li>
        <li
          ref={activeModule === "generate-bill" ? activeItemRef : null}
          className={`sidebar-item bill ${activeModule === "generate-bill" ? "active" : ""}`}
          onClick={() => handleNavigation("generate-bill", "/generate-bill")}
        >
          <MdAttachMoney className="icon" />
          {expanded && <span>Billing</span>}
        </li>
        <li
          ref={activeModule === "transactions" ? activeItemRef : null}
          className={`sidebar-item transactions ${activeModule === "transactions" ? "active" : ""}`}
          onClick={() => handleNavigation("transactions", "/transactions")}
        >
          <MdPayment className="icon" />
          {expanded && <span>Transactions</span>}
        </li>
        <li
          ref={activeModule === "business" ? activeItemRef : null}
          className={`sidebar-item business ${activeModule === "business" ? "active" : ""}`}
          onClick={() => handleNavigation("business", "/business")}
        >
          <MdAssignment className="icon" />
          {expanded && <span>Business</span>}
        </li>
        <li
          ref={activeModule === "revise-bill" ? activeItemRef : null}
          className={`sidebar-item revise-bill ${activeModule === "revise-bill" ? "active" : ""}`}
          onClick={() => handleNavigation("revise-bill", "/revise-bill")}
        >
          <MdEdit className="icon" />
          {expanded && <span>Revise Bill</span>}
        </li>
        <li
          ref={activeModule === "order-book" ? activeItemRef : null}
          className={`sidebar-item order-book ${activeModule === "order-book" ? "active" : ""}`}
          onClick={() => handleNavigation("order-book", "/order-book")}
        >
          <MdLibraryBooks className="icon" />
          {expanded && <span>Order Book</span>}
        </li>
        <li
          ref={activeModule === "task" ? activeItemRef : null}
          className={`sidebar-item task ${activeModule === "task" ? "active" : ""}`}
          onClick={() => handleNavigation("task", "/task")}
        >
          <MdChecklist className="icon" />
          {expanded && <span>Task</span>}
        </li>
        <li
          ref={activeModule === "approval" ? activeItemRef : null}
          className={`sidebar-item approval ${activeModule === "approval" ? "active" : ""}`}
          onClick={() => handleNavigation("approval", "/approval")}
        >
          <MdHowToReg className="icon" />
          {expanded && <span>Approval</span>}
        </li>
      </ul>

      <div className={`sidebar-footer ${locked ? "locked" : ""}`}>
        <div className="user-profile" onClick={toggleLock}>
          <MdAccountCircle className="profile-icon" />
          {expanded && (
            <div
              className="profile-info-expanded"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/profile");
              }}
            >
              <span className="profile-name">Hello, {username}</span>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;